import { WorkflowStateService } from '@shared/state/workflow-state.service';
import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  signal,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { Signal } from '@angular/core';
import { NodeClass } from '@modules/campaigns/Domain/NodeClass';
import { NodeInspectorRegistry } from '@shared/state/node-inspector-registry.service';
import { NgIf } from '@angular/common';
import { Subscription } from 'rxjs';
import { NodeFactory } from '@modules/campaigns/Domain/Factories/NodeFactory';
import { SidebarModule } from 'primeng/sidebar';
import { NodeInspectorRegistrar } from '@shared/components/joint-js/components/inspectors/node-inspector/Service/node-inspector-registrar.service';
import { INodeInspectorAdapter } from '@shared/components/joint-js/services/INodeInspectorAdapter';

export type InspectorMode = 'edit' | 'preview';

@Component({
  selector: 'app-node-inspector-new',
  templateUrl: './node-inspector.component.html',
  styleUrls: ['./node-inspector.component.scss'],
  standalone: true,
  imports: [NgIf, SidebarModule],
})
export class NodeInspectorComponent2 implements OnInit, OnDestroy, OnChanges {
  @Input() mode: InspectorMode = 'edit';
  @Output() nodeGoalToSelect = new EventEmitter<void>();
  @ViewChild('host', { read: ViewContainerRef, static: true }) host!: ViewContainerRef;
  private created?: ComponentRef<any>;
  private subscription?: Subscription;

  isOpen = false;

  constructor(
    private workflow: WorkflowStateService,
    private registry: NodeInspectorRegistry,
    private register: NodeInspectorRegistrar, //needed to inject to register the inspectors
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['mode'] && !changes['mode'].firstChange && this.created) {
      const instance = this.created.instance as INodeInspectorAdapter;
      instance.setMode(this.mode);
    }
  }

  ngOnInit() {
    this.subscription = this.workflow.selectedNodeEvent$.subscribe((eventNode) => {
      if (!eventNode) {
        Promise.resolve().then(() => {
          this.host.clear();
          this.created?.destroy();
          this.isOpen = false;
        });
        return;
      }

      let nodeSignal: Signal<NodeClass | null>;
      let nodeValue: NodeClass | null = null;

      if (eventNode.type === 'existing') {
        nodeSignal = this.workflow.getNodeSignal(eventNode.nodeId);
      } else if (eventNode.type === 'dragged' && eventNode.nodeData) {
        nodeValue = NodeFactory.createNode(
          NodeFactory.mapStringToNodeType(eventNode.nodeData.name)!,
          eventNode.nodeData.position?.x ?? 0,
          eventNode.nodeData.position?.y ?? 0,
          eventNode.nodeId,
        );
        (nodeValue?.config as any).cases = eventNode.nodeData.cases ?? {};

        nodeSignal = signal<NodeClass | null>(nodeValue);
      } else {
        return;
      }

      const compType = this.registry.get(nodeSignal()?.type!);

      if (!compType) return;

      Promise.resolve().then(() => {
        this.host.clear();
        this.created?.destroy();

        this.created = this.host.createComponent(compType);

        const instance = this.created.instance as INodeInspectorAdapter;
        instance.setNode(nodeSignal);
        instance.setMode(this.mode);
        if (eventNode.type === 'dragged') instance.setActiveTab?.('config');

        if ((instance as any).nodeGoalToSelect) {
          (instance as any).nodeGoalToSelect.subscribe(() => {
            this.nodeGoalToSelect.emit();
          });
        }

        instance.closeSidebar.subscribe(() => {
          this.handleSaved();
        });

        this.isOpen = true;
      });
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.created?.destroy();
    this.host.clear();
  }

  onSidebarHide() {
    this.workflow.setSelectedNode(null);
  }

  protected handleSaved() {
    this.isOpen = false;
    this.workflow.setSelectedNode(null);

    this.host.clear();
    this.created?.destroy();
    this.created = undefined;
  }
}
